import { IoSettings } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { ICON_MAP } from '../../shared/constants/iconMap';
import { useMenu } from '../../shared/context/MenuContext';

interface DesktopSidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isActive: (path: string) => boolean;
}

function DesktopSidebar({ isOpen, setIsOpen, isActive }: DesktopSidebarProps) {
  const { menuItems, isLoading, userType, partnerName, error } = useMenu();

  if (isLoading) {
    return (
      <div
        className='transition-all duration-300 ease-in-out border-r border-gray-200 h-screen relative z-30 hidden md:flex flex-shrink-0 bg-white'
        style={{ width: isOpen ? '14rem' : '4rem' }}
      >
        <div className='p-4 text-center text-gray-500'>메뉴 로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className='transition-all duration-300 ease-in-out border-r border-gray-200 h-screen relative z-30 hidden md:flex flex-shrink-0 bg-white'
        style={{ width: isOpen ? '14rem' : '4rem' }}
      >
        <div className='p-4 text-center text-red-500'>
          메뉴 로딩 실패
          <br />
          <small>{error.message}</small>
        </div>
      </div>
    );
  }

  return (
    <div
      className='transition-all duration-300 ease-in-out border-r border-gray-200 h-screen bg-white flex flex-col relative z-30 hidden md:flex flex-shrink-0'
      style={{ width: isOpen ? '14rem' : '4rem' }}
    >
      {/* 헤더 영역 (고정) */}
      <div className='flex-shrink-0'>
        <div
          className={`flex items-center px-6 py-3 border-b h-16 ${
            isOpen ? 'justify-between' : 'justify-center'
          }`}
        >
          {isOpen ? (
            <>
              <Link to='/admin/dashboard'>
                <h2 className='font-bold text-xl text-punta-orange'>
                  PuntaCompany
                </h2>
              </Link>
              <button
                type='button'
                onClick={() => setIsOpen(false)}
                className='p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500'
              >
                ◀
              </button>
            </>
          ) : (
            <button
              type='button'
              onClick={() => setIsOpen(true)}
              className='p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500'
            >
              ▶
            </button>
          )}
        </div>

        {/* 파트너사 정보 표시 */}
        {userType === 'partner' && partnerName && isOpen && (
          <div className='px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 flex-shrink-0'>
            <div className='flex items-center mb-2'>
              <div className='w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3'>
                <span className='text-white text-sm font-bold'>
                  {partnerName.charAt(0)}
                </span>
              </div>
              <div>
                <div className='text-sm font-semibold text-gray-800'>
                  {partnerName}
                </div>
                <div className='text-xs text-blue-600 font-medium'>
                  파트너사 관리자
                </div>
              </div>
            </div>
            <div className='flex items-center text-xs text-gray-600'>
              <span className='inline-block w-2 h-2 bg-green-400 rounded-full mr-2' />
              온라인
            </div>
          </div>
        )}

        {/* 관리자 정보 표시 */}
        {userType !== 'partner' && isOpen && (
          <div className='px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex-shrink-0'>
            <div className='flex items-center mb-2'>
              <div className='w-8 h-8 bg-punta-orange rounded-full flex items-center justify-center mr-3'>
                <span className='text-white text-sm font-bold'>관</span>
              </div>
              <div>
                <div className='text-sm font-semibold text-gray-800'>
                  관리자
                </div>
                <div className='text-xs text-orange-600 font-medium'>
                  시스템 관리자
                </div>
              </div>
            </div>
            <div className='flex items-center text-xs text-gray-600'>
              <span className='inline-block w-2 h-2 bg-green-400 rounded-full mr-2' />
              온라인
            </div>
          </div>
        )}
      </div>

      {/* 헤더 높이 맞춤용 공간 */}
      {(!isOpen || (userType === 'partner' && !partnerName)) && (
        <div
          className='border-b border-gray-200 flex-shrink-0'
          style={{ height: '80px' }}
        />
      )}

      {/* 메뉴 영역 (스크롤 가능) */}
      <div className='flex-1 overflow-hidden'>
        <div
          className='h-full overflow-y-auto sidebar-scroll'
          style={{ touchAction: 'pan-y' }}
        >
          <nav className='py-4 pb-4'>
            {menuItems.map((item: any) => (
              <Link
                key={item.id}
                to={item.path}
                className={`
                  flex items-center px-6 py-3 text-gray-700 hover:bg-orange-50
                  transition-all duration-200 relative group
                  ${isOpen ? 'justify-start' : 'justify-center'}
                  ${isActive(item.path) && 'bg-orange-50 text-punta-orange'}
                `}
              >
                <div className='flex items-center'>
                  {/* 아이콘 매핑 적용 */}
                  {ICON_MAP[item.icon] || <IoSettings className='w-6 h-6' />}
                  <span
                    className={`ml-3 transition-opacity duration-300 ${
                      !isOpen && 'hidden'
                    }`}
                  >
                    {item.title}
                  </span>
                </div>

                {!isOpen && (
                  <div className='absolute left-full ml-2 px-3 py-2 bg-gray-800 text-white rounded-md text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none'>
                    {item.title}
                  </div>
                )}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

export default DesktopSidebar;
